import type { Question, TestConfig } from "@shared/schema";
import {
  SINGLE_ITEM_CEILING_SECONDS,
  SUBMISSION_OVERHEAD_SECONDS,
} from "@shared/generationTiming";

// ---------------------------------------------------------------------------
// Bounded generation requests, shared by every client caller of the Blacksmith
// endpoints.
//
// Lifted out of GenerateTest.tsx unchanged (ARC-30) so that "Add item" gets the
// same discipline instead of a second, weaker copy of it. The comments below
// are the ARC-26 review findings; they are load-bearing, not decoration.
// ---------------------------------------------------------------------------

/**
 * Carries the HTTP status alongside the message so a poll loop can tell a
 * transient failure (502/504, network blip) from a permanent one (401 expired
 * token, 404 unknown session). Retrying a 401 every 3s until a watchdog trips
 * shows the user a healthy-looking spinner while nothing can ever succeed
 * (ARC-26 cause C).
 */
export class HttpError extends Error {
  status: number;
  /** Machine-readable `code` from the response body, when the server sent one. */
  code?: string;
  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = "HttpError";
    this.status = status;
    this.code = code;
  }
}

export async function readJsonOrThrow(response: Response): Promise<any> {
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: "Unknown error" }));
    const base = error.error || `HTTP ${response.status}`;
    throw new HttpError(
      error.details ? `${base}: ${error.details}` : base,
      response.status,
      typeof error.code === "string" ? error.code : undefined,
    );
  }
  return response.json();
}

/**
 * Every generation call gets a hard ceiling. Without one, a request that HANGS
 * rather than failing is indistinguishable from work in progress — and any
 * guard that suppresses a watchdog while a request is in flight then becomes a
 * way to switch the watchdog off permanently. That is precisely how the
 * original ARC-26 cause-B hang worked, and it is easy to rebuild by accident,
 * so the bound lives at the fetch itself rather than in any one caller.
 */
export async function requestJson(
  input: string,
  init: RequestInit,
  timeoutMs: number,
  label: string,
): Promise<any> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetch(input, { ...init, signal: controller.signal });
    // The body is read INSIDE the timeout window deliberately. fetch()
    // resolves as soon as the response HEADERS arrive, so clearing the timer
    // at that point would bound only the headers and leave a stalled body
    // completely unbounded — a permanent hang with no timer behind it, which
    // is the entire class of bug ARC-26 was about. Found in review after
    // exactly that mistake shipped in the first cut of that fix.
    return await readJsonOrThrow(response);
  } catch (err) {
    if (controller.signal.aborted) {
      throw new Error(`${label} did not respond within ${Math.round(timeoutMs / 1000)}s`);
    }
    throw err;
  } finally {
    clearTimeout(timer);
  }
}

/** Turn a non-OK Response into an HttpError carrying its status and `code`. */
export async function httpErrorFrom(response: Response): Promise<HttpError> {
  const body = await response.json().catch(() => ({} as any));
  const base = body?.error || `HTTP ${response.status}`;
  return new HttpError(
    body?.details ? `${base}: ${body.details}` : base,
    response.status,
    typeof body?.code === "string" ? body.code : undefined,
  );
}

/**
 * Toast copy for a failed single-item regeneration.
 *
 * ARC-30 cap 2: the server now answers 504 `generation_timeout` when it gave up
 * WAITING on an item BCE is still writing, as distinct from a 500 when
 * something actually broke. Both used to arrive as "Regeneration Failed", which
 * is false for the first one and pushes the user into re-running work that is
 * already in flight.
 */
export function regenerationToast(
  err: unknown,
  failureDescription: string,
): { title: string; description: string } {
  if (err instanceof HttpError && (err.status === 504 || err.code === "generation_timeout")) {
    return {
      title: "Still generating",
      description:
        "This one is taking longer than usual and hasn't failed — Blacksmith is still writing it. " +
        "Give it a moment, then try again.",
    };
  }
  return { title: "Regeneration Failed", description: failureDescription };
}

export function authHeaders(token?: string | null): Record<string, string> {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  return headers;
}

// ---------------------------------------------------------------------------
// "Add item" — one item, generated with live progress (ARC-30 cap 3)
// ---------------------------------------------------------------------------

const START_TIMEOUT_MS = 60_000;
const STATUS_TIMEOUT_MS = 15_000;
const FINISH_TIMEOUT_MS = 90_000;
const POLL_INTERVAL_MS = 3000;
/**
 * Absolute ceiling on one added item: the single-item budget plus the
 * submission overhead that is not per-item. ~7 minutes — close to what the old
 * blocking route would have spent anyway, the difference being that every
 * second of it is now visible and interruptible instead of being a silent
 * held-open POST.
 */
const CEILING_MS = (SINGLE_ITEM_CEILING_SECONDS + SUBMISSION_OVERHEAD_SECONDS) * 1000;
/** ~1 minute of unbroken failure before we stop calling it transient. */
const MAX_CONSECUTIVE_POLL_FAILURES = 20;
/**
 * Consecutive failures before the UI mentions it (~9s). Matching
 * GenerateTest's POLL_TROUBLE_AFTER: one dropped poll is noise, and reporting
 * every blip trains people to ignore the message that matters.
 */
const POLL_TROUBLE_AFTER = 3;

export type AddItemPhase = "submitting" | "generating" | "retrieving";

export type AddItemProgress = {
  phase: AddItemPhase;
  elapsedMs: number;
  /** True once the poll has been failing repeatedly but is still retrying. */
  degraded: boolean;
};

export type AddItemRequest = {
  topic: { id: string; name: string; standards?: unknown };
  level: "knowledge" | "understanding" | "higherOrderThinking";
  config: TestConfig;
  /** All current items on the test, for the server's cross-item duplication
   *  check. Threaded through the finish request so the check can flag the
   *  item before it lands on the card. */
  existingItems?: Array<{ stem: string; answers: string[] }>;
};

/** Server-side cross-item duplication warning, when the check flags one. */
export type DuplicateWarning = {
  flagged: boolean;
  reasons: string[];
};

/**
 * Thrown when the ceiling is reached with BCE still working. The item is not
 * lost — it is still being generated server-side — so callers must not present
 * this as a failure.
 */
export class AddItemTimeoutError extends Error {
  readonly sessionId: string;
  constructor(sessionId: string, elapsedMs: number) {
    super(
      `Still generating after ${Math.round(elapsedMs / 1000)}s (session: ${sessionId}). ` +
        `It hasn't failed — we stopped waiting.`,
    );
    this.name = "AddItemTimeoutError";
    this.sessionId = sessionId;
  }
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Submit one item, poll BCE for its status, retrieve it when done.
 *
 * Replaces a single POST that held the connection open for up to 8 minutes
 * with no way for the UI to say anything true about what was happening. Calls
 * `onProgress` on submission and on every poll, so the caller can show elapsed
 * time and phase rather than an undifferentiated spinner.
 */
export async function generateSingleItem(
  request: AddItemRequest,
  opts: {
    token?: string | null;
    onProgress?: (progress: AddItemProgress) => void;
    /** Set by the caller to abandon the wait (e.g. the page unmounted). */
    shouldStop?: () => boolean;
  } = {},
): Promise<{ question: Question; duplicate_warning?: DuplicateWarning }> {
  const { token, onProgress, shouldStop } = opts;
  const startedAt = Date.now();
  const report = (phase: AddItemPhase, degraded = false) =>
    onProgress?.({ phase, elapsedMs: Date.now() - startedAt, degraded });

  report("submitting");
  const { sessionId } = (await requestJson(
    "/api/questions/add/start",
    { method: "POST", headers: authHeaders(token), body: JSON.stringify(request) },
    START_TIMEOUT_MS,
    "Starting item generation",
  )) as { sessionId: string };

  if (!sessionId) throw new Error("Item generation did not return a session");

  let consecutiveFailures = 0;

  while (true) {
    if (shouldStop?.()) throw new AddItemTimeoutError(sessionId, Date.now() - startedAt);
    if (Date.now() - startedAt > CEILING_MS) {
      throw new AddItemTimeoutError(sessionId, Date.now() - startedAt);
    }

    await sleep(POLL_INTERVAL_MS);

    let status: { status: string };
    try {
      // Batch generation's status route, reused as-is: it is a plain BCE
      // status passthrough keyed by session, with nothing batch-specific in
      // it.
      status = (await requestJson(
        `/api/generate-questions/status/${sessionId}`,
        { headers: authHeaders(token) },
        STATUS_TIMEOUT_MS,
        "Status check",
      )) as { status: string };
      consecutiveFailures = 0;
    } catch (err) {
      // Permanent failures are permanent — retrying a 401 or a 404 until the
      // ceiling just shows a healthy spinner over something that can never
      // succeed (ARC-26 cause C, same rule as the batch poll).
      const httpStatus = err instanceof HttpError ? err.status : undefined;
      if (httpStatus === 401 || httpStatus === 403 || httpStatus === 404) throw err;
      consecutiveFailures += 1;
      if (consecutiveFailures >= MAX_CONSECUTIVE_POLL_FAILURES) throw err;
      report("generating", consecutiveFailures >= POLL_TROUBLE_AFTER);
      continue;
    }

    if (status.status === "processing") {
      report("generating");
      continue;
    }

    // Anything that is not "processing" is worth TRYING to retrieve rather than
    // polling through a status we do not recognise until the ceiling (ARC-26
    // cause D). /finish is the arbiter: it answers 409 `still_processing` for
    // anything that is neither done nor explicitly failed, which puts us
    // straight back in this loop, and a real error for a real failure.
    report("retrieving");
    try {
      const result = (await requestJson(
        "/api/questions/add/finish",
        {
          method: "POST",
          headers: authHeaders(token),
          body: JSON.stringify({ ...request, sessionId }),
        },
        FINISH_TIMEOUT_MS,
        "Retrieving your item",
      )) as { question?: Question; duplicate_warning?: DuplicateWarning };
      if (!result?.question) throw new Error("Item generation returned no question");
      return { question: result.question, duplicate_warning: result.duplicate_warning };
    } catch (err) {
      if (err instanceof HttpError && err.code === "still_processing") {
        report("generating");
        continue;
      }
      throw err;
    }
  }
}
